import {
  React,
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  EditIcon,
  DeleteIcon,
} from "../../utils/commonImports";

/* ------------------------------------------------------------------ */
/* Compact design tokens (same as the other forms)                     */
/* ------------------------------------------------------------------ */
const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  border: "#e2e8f0",
  surface: "#f8fafc",
  selected: "#eff6ff",
  text: "#0f172a",
  muted: "#64748b",
};

const fieldSx = {
  "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { py: "7px" },
  "& .MuiSelect-select": { py: "7px !important" },
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiInputLabel-shrink": { fontSize: 13 },
};

const thStyle = {
  textAlign: "left",
  fontWeight: 600,
  color: C.muted,
  padding: "8px 10px",
  borderBottom: `1px solid ${C.border}`,
  whiteSpace: "nowrap",
};

const tdStyle = { padding: "6px 10px" };

const Badge = ({ color, background, children }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: 10,
      color,
      background,
    }}
  >
    {children}
  </span>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
function EmployeeBankSection({
  bankInput,
  banks,
  handleBankChange,
  addBank,
  editBank,
  deleteBank,
}) {
  const isUpdate = !!bankInput?.bankid;

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {/* ---------- Add / edit form ---------- */}
      <Box
        sx={{
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            minHeight: 40,
            borderBottom: `1px solid ${C.border}`,
            backgroundColor: C.surface,
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {isUpdate ? "Edit bank account" : "Add bank account"}
          </Typography>
          <Button
            type="button"
            variant="contained"
            onClick={addBank}
            sx={{
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              borderRadius: "6px",
              boxShadow: "none",
              minWidth: 0,
              px: 2,
              py: 0.5,
              backgroundColor: C.primary,
              "&:hover": { backgroundColor: C.primaryHover },
            }}
          >
            {isUpdate ? "Update bank" : "Add bank"}
          </Button>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(6, 1fr)",
            },
          }}
        >
          <TextField
            label="Bank name"
            name="EmployeeBankName"
            size="small"
            fullWidth
            value={bankInput.EmployeeBankName || ""}
            onChange={handleBankChange}
            sx={fieldSx}
          />
          <TextField
            select
            label="Account type"
            name="EmployeeAccountType"
            size="small"
            fullWidth
            value={bankInput.EmployeeAccountType || ""}
            onChange={handleBankChange}
            sx={fieldSx}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>
              Select type
            </MenuItem>
            <MenuItem value="Savings Account" sx={{ fontSize: 13 }}>
              Savings Account
            </MenuItem>
            <MenuItem value="Current Account" sx={{ fontSize: 13 }}>
              Current Account
            </MenuItem>
          </TextField>
          <TextField
            label="Account number"
            name="EmployeeAccountnumber"
            size="small"
            fullWidth
            value={bankInput.EmployeeAccountnumber || ""}
            onChange={handleBankChange}
            sx={fieldSx}
          />
          <TextField
            label="Branch"
            name="EmployeeBranch"
            size="small"
            fullWidth
            value={bankInput.EmployeeBranch || ""}
            onChange={handleBankChange}
            sx={fieldSx}
          />
          <TextField
            label="IFSC"
            name="EmployeeBankIFSC"
            size="small"
            fullWidth
            value={bankInput.EmployeeBankIFSC || ""}
            onChange={handleBankChange}
            sx={fieldSx}
          />
          <TextField
            select
            label="Active"
            name="BankActive"
            size="small"
            fullWidth
            value={bankInput.BankActive || "Yes"}
            onChange={handleBankChange}
            sx={fieldSx}
          >
            <MenuItem value="Yes" sx={{ fontSize: 13 }}>
              Yes
            </MenuItem>
            <MenuItem value="No" sx={{ fontSize: 13 }}>
              No
            </MenuItem>
          </TextField>
        </Box>
      </Box>

      {/* ---------- Bank accounts table ---------- */}
      <Box
        sx={{
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
          overflowX: "auto",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead>
            <tr style={{ backgroundColor: C.surface }}>
              <th style={thStyle}>Bank</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>A/C no.</th>
              <th style={thStyle}>Branch</th>
              <th style={thStyle}>IFSC</th>
              <th style={thStyle}>Active</th>
              <th style={{ ...thStyle, width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {banks.map((bank, index) => (
              <tr key={index} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ ...tdStyle, fontWeight: 500 }}>
                  {bank.EmployeeBankName}
                  {!bank.bankid && (
                    <span style={{ marginLeft: 8 }}>
                      <Badge color="#166534" background="#dcfce7">
                        New
                      </Badge>
                    </span>
                  )}
                </td>
                <td style={tdStyle}>{bank.EmployeeAccountType}</td>
                <td style={tdStyle}>{bank.EmployeeAccountnumber}</td>
                <td style={tdStyle}>{bank.EmployeeBranch}</td>
                <td style={tdStyle}>{bank.EmployeeBankIFSC}</td>
                <td style={tdStyle}>
                  {bank.BankActive === "No" ? (
                    <Badge color={C.muted} background="#f1f5f9">
                      No
                    </Badge>
                  ) : (
                    <Badge color="#166534" background="#dcfce7">
                      Yes
                    </Badge>
                  )}
                </td>
                <td
                  style={{ ...tdStyle, padding: "2px 6px", whiteSpace: "nowrap" }}
                >
                  {/* IconButton renders type="button", so it never submits the parent form */}
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => editBank(index)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteBank(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </td>
              </tr>
            ))}
            {banks.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", color: C.muted, padding: 20 }}
                >
                  No bank accounts yet. Fill in the form above and click “Add
                  bank”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

export default EmployeeBankSection;
